<template>
  <m-spinner
    v-if="chartDataQuery.loading.value"
    class="_spinner"
  />
  <m-table
    v-else
    :class="$attrs.class"
    :data-source="data"
    :columns="columns"
    sticky-header
  />
</template>

<script setup>
import useChartDisplay from '@/composables/chart-display';
import useGoalProgressChart from '@/composables/grid-page/chart/goal-progress-chart';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useStatusProperty from '@/composables/goal/status-property';
import { DateTime } from 'luxon';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, chartGroupByOption, gridPageChartType } from 'shared/constants.json';
import { computed, toRef } from 'vue';
import { edgeName } from '@/composables/grid-page/chart/utils';
import { getRoundedValue } from '@/lib/charts/format';
import { iconByType } from '@/lib/property';
import { sortAlphaNumeric } from 'shared/lib/sort';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chart: {
    type: Object,
    required: true,
  },
  chartDataQuery: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { userLang } = useLoggedInUser();

const { metricUnit, getValueText } = useChartDisplay();
const { calculateExpectedValue } = useGoalProgressChart(toRef(props.chartDataQuery, 'result'));
const { statusProperty: goalStatusProperty } = useGoalProperty();
const { leafOptions: distributionStatusOptions } = useStatusProperty(goalStatusProperty, userLang);

const aggregator = computed(() => props.chart.aggregationConfig?.aggregation?.aggregator);
const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(aggregator.value));

const data = computed(() => {
  const result = props.chartDataQuery.result.value;
  if (result === null) {
    return [];
  }

  if (isTimeSeriesMode.value && result.isTimeSeries) {
    switch (aggregator.value) {
      case aggregatorType.progressOverTime: {
        if (!result.progressData || result.progressData.length === 0) {
          return [];
        }
        return result.progressData
          .filter((point) => point.x >= result.startDate.toMillis() && point.x <= result.endDate.toMillis())
          .sort((a, b) => a.x - b.x)
          .map((point) => ({
            date: DateTime.fromMillis(point.x).toLocaleString(DateTime.DATE_MED),
            actual: `${getRoundedValue(point.y, 2)} %`,
            expected: `${calculateExpectedValue(point.x, result.startDate, result.endDate)} %`,
          }));
      }
      case aggregatorType.statusScoreOverTime: {
        if (!result.statusScoreData || result.statusScoreData.length === 0) {
          return [];
        }
        return result.statusScoreData
          .filter((point) => point.x >= result.startDate.toMillis() && point.x <= result.endDate.toMillis())
          .sort((a, b) => a.x - b.x)
          .map((point) => ({
            date: DateTime.fromMillis(point.x).toLocaleString(DateTime.DATE_MED),
            actual: getRoundedValue(point.y, 1),
          }));
      }
      case aggregatorType.statusDistributionOverTime: {
        if (!result.statusDistributionData || result.statusDistributionData.length === 0) {
          return [];
        }
        return result.statusDistributionData
          .filter((point) => point.x >= result.startDate.toMillis() && point.x <= result.endDate.toMillis())
          .sort((a, b) => a.x - b.x)
          .map((point) => {
            const row = { date: DateTime.fromMillis(point.x).toLocaleString(DateTime.DATE_MED) };
            distributionStatusOptions.value.forEach((opt) => {
              row[`status_${opt.uid}`] = point.statusCounts[opt.uid] || 0;
            });
            return row;
          });
      }
      default:
        return [];
    }
  }

  if (props.chart.chartType === gridPageChartType.metric) {
    return [{
      name: metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source),
      y: result,
    }];
  }

  if (!Array.isArray(result.series)) {
    return [];
  }

  if (props.chart.chartType === gridPageChartType.pie) {
    return result.series[0].data;
  }

  return result.series.map((series) => {
    const row = { name: series.name ?? getValueText(props.chart.aggregationConfig.aggregation) };
    series.data.forEach((item, index) => {
      row[`col_${index}`] = item.y;
    });
    return row;
  });
});

const firstColumnTitle = computed(() => {
  if (props.chart.chartType === gridPageChartType.metric) {
    return t('gridPageChartDataTable.metric');
  }
  if (props.chart.chartType === gridPageChartType.pie) {
    return edgeName(props.chart.groupBy, t, userLang.value);
  }
  if (props.chart.aggregationConfig.stackOptions.groupBy === chartGroupByOption.none) {
    return t('gridPageChartDataTable.metric');
  }
  return edgeName(props.chart.aggregationConfig.stackOptions, t, userLang.value);
});

const firstColumnIcon = computed(() => {
  if (props.chart.chartType === gridPageChartType.metric || props.chart.groupBy === null) {
    return '';
  }

  const groupBy = props.chart.chartType === gridPageChartType.pie
    ? props.chart.groupBy
    : props.chart.aggregationConfig.stackOptions;

  if (groupBy.groupBy === chartGroupByOption.none) {
    return '';
  }

  return groupBy.isProperty
    ? iconByType({ type: groupBy.property.type })
    : iconByType({ type: groupBy.edgeType, edgeName: groupBy.edge });
});

const firstColumn = computed(() => ({
  key: 'name',
  dataIndex: 'name',
  title: firstColumnTitle.value,
  icon: firstColumnIcon.value,
  onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
  sorter: sortAlphaNumeric,
  sticky: true,
}));

const columns = computed(() => {
  const result = props.chartDataQuery.result.value;

  if (isTimeSeriesMode.value && result?.isTimeSeries) {
    const dateColumn = {
      key: 'date',
      dataIndex: 'date',
      title: t('gridPageChartDataTable.date'),
      sorter: sortAlphaNumeric,
      sticky: true,
    };
    const actualColumn = {
      key: 'actual',
      dataIndex: 'actual',
      title: t('gridPageChartDataTable.actual'),
      sorter: sortAlphaNumeric,
    };

    switch (aggregator.value) {
      case aggregatorType.progressOverTime:
        return [
          dateColumn,
          actualColumn,
          {
            key: 'expected',
            dataIndex: 'expected',
            title: t('gridPageChartDataTable.expected'),
            sorter: sortAlphaNumeric,
          },
        ];
      case aggregatorType.statusScoreOverTime:
        return [dateColumn, actualColumn];
      case aggregatorType.statusDistributionOverTime:
        return [
          dateColumn,
          ...distributionStatusOptions.value.map((opt) => ({
            key: `status_${opt.uid}`,
            dataIndex: `status_${opt.uid}`,
            title: textByLang(opt.label, userLang.value),
            sorter: sortAlphaNumeric,
          })),
        ];
      default:
        return [];
    }
  }

  if (!Array.isArray(result?.series)) {
    return [];
  }

  const valueColumn = {
    key: 'value',
    dataIndex: 'y',
    sorter: sortAlphaNumeric,
  };

  if (props.chart.chartType === gridPageChartType.metric) {
    return [firstColumn.value, { ...valueColumn, title: t('gridPageChartDataTable.value') }];
  }

  if (props.chart.chartType === gridPageChartType.pie) {
    return [firstColumn.value, { ...valueColumn, title: getValueText(props.chart.aggregationConfig.aggregation, 'aggregator') }];
  }

  const sorter = result.series.length > 1 ? sortAlphaNumeric : undefined;
  return [
    firstColumn.value,
    ...(result.categories ?? []).map((category, index) => ({
      key: `col_${index}`,
      dataIndex: `col_${index}`,
      title: category,
      sorter,
    })),
  ];
});
</script>

<style scoped lang="scss">
._spinner {
  margin-top: 3rem;
}
</style>
